import React from "react";
import glamorous from "glamorous";

import FeatureButton from "../FeatureButton";
import {
  Close as CloseIcon,
  Messenger as MessengerIcon
} from "../FeatureIcons";

const ContactFormOverlay = glamorous.div(({ isOpen }) => ({
  alignItems: "center",
  background: "rgba(255,255,255,0.8)",
  display: isOpen ? "flex" : "none",
  height: "100%",
  justifyContent: "center",
  position: "fixed",
    top: 0,
    left: 0,
  width: "100%",
  zIndex: 3
}));

const ContactFormContainer = glamorous.div(({ theme }) => ({
  background: theme.main.colors.light,
  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
  padding: "0.5rem",
  transition: "0.25s",
  width: "50%",
  [theme.main.mediaQueries.tablet]: {
    width: "75%"
  },
  [theme.main.mediaQueries.mobile]: {
    width: "95%"
  }
}));

const Form = glamorous.form(({ isShown, theme }) => ({
  display: isShown ? "block" : "none",
  padding: "2rem 1rem",
  "& input[type='text'], input[type='email'], textarea": {
    border: "1px solid #eee",
    fontSize: "0.8rem",
    padding: "0.25rem 0.5rem",
    marginBottom: "0.5rem",
    width: "32%",
    [theme.main.mediaQueries.mobile]: {
      width: "100%"
    }
  },
  "& textarea": {
    padding: "0.5rem",
    minWidth: "100%",
    maxWidth: "100%",
    width: "100%"
  },
  "& input[disabled], textarea[disabled]": {
    opacity: 0.5
  }
}));

const FormInputContainer = glamorous.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  [theme.main.mediaQueries.mobile]: {
    flexDirection: "column"
  }
}));

const ContactFormSubmitButton = glamorous.input(({ disabled }) => ({
  background: "#A9DCE3",
  border: "none",
  boxShadow: "0px 2px 5px rgba(255, 255, 255, 0.25)",
  fontWeight: 800,
  fontSize: "0.9rem",
  padding: "0.5rem 1.5rem",
  textTransform: "uppercase",
  transition: "0.25s",
  ":hover": {
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 1 : 0.8,
    transform: disabled ? "none" : "scale(1.02, 1.02)",
    transition: "0.25s"
  }
}));

const ContactMethodSelection = glamorous.div(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  padding: "2rem",
  [theme.main.mediaQueries.mobile]: {
    flexDirection: "column",
    "&> *:first-child": {
      marginBottom: "1rem"
    }
  }
}));

const MessengerButton = glamorous(FeatureButton)(({ theme }) => ({
  marginRight: "1rem",
  textAlign: "center",
  [theme.main.mediaQueries.mobile]: {
    marginRight: 0,
    textAlign: "center",
    width: "100%"
  }
}));

const EmailButton = glamorous(FeatureButton)(({ theme }) => ({
  background: "#fff",
  boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
  textAlign: "center",
  color: theme.main.colors.ocean,
  ":hover": {
    background: "#fff",
    color: theme.main.colors.ocean,
  },
  [theme.main.mediaQueries.mobile]: {
    textAlign: "center",
    width: "100%"
  }
}));

const encode = data => Object.keys(data)
  .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
  .join("&");

class ContactForm extends React.Component {
  constructor () {
    super();
    this.state = {
      showEmailForm: false,
      emailFormPending: false,
      emailFormSubmitted: false,
      form: {}
    };
  }

  toggleEmailForm (event) {
    event.preventDefault();
    this.setState({
      showEmailForm: !this.state.showEmailForm
    });
  }

  onChange = event => {
    this.setState({
      form: {
        ...this.state.form,
        [event.target.name]: event.target.value
      }
    });
  };

  onFormSubmit = event => {
    event.preventDefault();

    this.setState({
      emailFormPending: true
    });

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact", ...this.state.form })
    })
      .then(() => {
        this.setState({
          emailFormPending: false,
          emailFormSubmitted: true
        });
      })
      .catch(error => {
        /* eslint-disable no-console */
        console.error(error);
        /* eslint-enable no-console */
        this.setState({
          emailFormPending: false
        });
      });
  };

  render () {
    const { onToggleContactForm, isOpen } = this.props;
    const { showEmailForm, emailFormPending, emailFormSubmitted } = this.state;
    const toggleEmailForm = this.toggleEmailForm.bind(this);
    const disableFormInputs = emailFormPending || emailFormSubmitted;
    return (
      <ContactFormOverlay isOpen={isOpen}>
        <ContactFormContainer>
          <glamorous.Div display="flex" justifyContent="flex-end">
            <a href="#" onClick={onToggleContactForm}><CloseIcon /></a>
          </glamorous.Div>
          <glamorous.H2 textAlign="center" marginBottom={0}>Get in touch</glamorous.H2>
          <Form
            action="/"
            method="post"
            name="contact"
            data-netlify="true"
            onSubmit={this.onFormSubmit}
            isShown={showEmailForm}
          >
            <FormInputContainer>
              <input placeholder="Full name" type="text" name="name" onChange={this.onChange} disabled={disableFormInputs} />
              <input placeholder="Email address" type="email" name="email" onChange={this.onChange} disabled={disableFormInputs} />
              <input placeholder="Institution / company" type="text" name="organization" onChange={this.onChange} disabled={disableFormInputs} />
            </FormInputContainer>
            <textarea name="message" placeholder="Your message..." rows={7} onChange={this.onChange} disabled={disableFormInputs} />
            <glamorous.Div display="flex" alignItems="center" justifyContent="space-between">
              <glamorous.A href="#" textDecoration="none" onClick={toggleEmailForm}>&laquo; Go back</glamorous.A>
              {emailFormSubmitted
                ? <glamorous.P marginBottom={0}>Thanks! We&apos;ll get back to you shortly.</glamorous.P>
                : <ContactFormSubmitButton
                    disabled={disableFormInputs}
                    type="submit"
                    value={emailFormPending ? "Sending..." : "Send"}
                  />
              }
            </glamorous.Div>
          </Form>
          {isOpen && !showEmailForm &&
            (
              <ContactMethodSelection>
                <MessengerButton target="blank" href="https://m.me/MantaEquipment?ref=contact-form">
                  <MessengerIcon />
                  <span>Talk to us on Messenger</span>
                </MessengerButton>
                <EmailButton onClick={toggleEmailForm}>Send us an email</EmailButton>
              </ContactMethodSelection>
            )
          }
        </ContactFormContainer>
      </ContactFormOverlay>
    );
  }
}

export default ContactForm;



// WEBPACK FOOTER //
// ./src/components/ContactForm/index.js